import { useAtomValue } from 'jotai';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { FormProvider, useForm, type ValidationRule } from 'react-hook-form';

import { Button, FlexContainer, FlexItem } from '@placer-ui/components';
import { RequestPropertyFormProps } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import { ControlledInputField } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-input-field';
import { csvAddressFields } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/file-based-request-property/file-fields-mapping-config';
import { supportRequestAddressTypeStore } from 'features/offline-reports/support-requests/store/property-store';
import { googleMapsUrlRules } from 'form-validations';
import {
    COORDINATES_ERROR_MESSAGES,
    LAT_REGEX,
    LONG_REGEX,
} from 'features/offline-reports/support-requests/consts';

export type NotMyPropertyFormValues = {
    name: string;
    formatted_address: string;
    lat: string;
    lng: string;
    zip_code: string;
    street: string;
    city: string;
    state: string;
    google_map_link: string;
    isDirty: boolean;
    selected_alt: number;
};

export const NotFoundOtherProperty = ({ onNextHandler, item }: RequestPropertyFormProps) => {
    const supportRequestAddressType = useAtomValue(supportRequestAddressTypeStore);

    const formController = useForm<NotMyPropertyFormValues>({
        defaultValues: {
            name: item?.name ?? '',
            formatted_address: item?.formatted_address ?? '',
            lat: item?.lat ?? '',
            lng: item?.lng ?? '',
            zip_code: item?.zip_code ?? '',
            street: item?.street ?? '',
            city: item?.city ?? '',
            state: item?.state ?? '',
            google_map_link: '',
            selected_alt: -1,
        },
        mode: 'onBlur',
    });

    const {
        formState: { dirtyFields },
    } = formController;

    const handleSubmit = (data: NotMyPropertyFormValues) => {
        const { google_map_link, ...relevantDirtyFields } = dirtyFields;
        onNextHandler?.({ ...data, isDirty: !isEmpty(relevantDirtyFields) });
    };

    return (
        <FormProvider {...formController}>
            <form
                onSubmit={(ev) => ev.preventDefault()}
                style={{ height: '100%', overflow: 'hidden' }}
            >
                <FlexContainer
                    flexDirection="column"
                    gap={8}
                    height="100%"
                    justifyContent="space-between"
                >
                    <div style={{ overflow: 'auto' }}>
                        <FlexContainer flexDirection="column" gap={8}>
                            <ControlledInputField
                                name="name"
                                label="Name"
                                placeholder="Name"
                                maxLength={100}
                                required
                                asterix
                                testId="ask-review-name"
                            />

                            {csvAddressFields[supportRequestAddressType!].map((item) => {
                                let validationPattern = {} as ValidationRule<RegExp>;
                                let inputType = 'text';

                                if (item.value === 'lat') {
                                    validationPattern = {
                                        value: LAT_REGEX,
                                        message: COORDINATES_ERROR_MESSAGES.lat,
                                    };
                                    inputType = 'number';
                                } else if (item.value === 'lng') {
                                    validationPattern = {
                                        value: LONG_REGEX,
                                        message: COORDINATES_ERROR_MESSAGES.lng,
                                    };
                                    inputType = 'number';
                                }

                                return (
                                    <ControlledInputField
                                        key={item.value}
                                        name={item.value}
                                        label={item.label}
                                        placeholder={item.label as string}
                                        maxLength={100}
                                        required
                                        asterix
                                        testId={`ask-review-${item.value}`}
                                        type={inputType}
                                        rules={
                                            validationPattern
                                                ? { pattern: validationPattern }
                                                : undefined
                                        }
                                    />
                                );
                            })}

                            <ControlledInputField
                                name="zip_code"
                                label={'Zip Code'}
                                placeholder="Briefly describe the edit needed"
                                maxLength={100}
                                testId="ask-review-zip-code"
                            />

                            <ControlledInputField
                                name="google_map_link"
                                label="Link to google map"
                                rules={googleMapsUrlRules}
                                placeholder="Type or paste the link"
                                maxLength={100}
                                testId="ask-review-gmap-link"
                            />
                        </FlexContainer>
                    </div>
                    <FlexItem alignSelf="flex-end">
                        <Button
                            disabled={!formController.formState.isValid}
                            variant="primary-gray"
                            size="small"
                            testId="submit-button"
                            onClick={formController.handleSubmit(handleSubmit)}
                        >
                            Confirm
                        </Button>
                    </FlexItem>
                </FlexContainer>
            </form>
        </FormProvider>
    );
};
